import { useEffect, useRef } from 'react';
import { renderToString } from 'react-dom/server';
import L from 'leaflet';
import type { Participant } from '@inloco/shared';
import type { RoomSession } from '../../../types/room-session';
import { VisitorMarker } from '../Markers';
import { getMarkerColor } from '../colors';
import type { TFunction } from 'i18next';
import { getParticipantPopupContent } from '@/utils/constants';

interface UseParticipantMarkersProps {
    mapRef: React.RefObject<L.Map | null>;
    participants: Participant[];
    roomSession: RoomSession | null;
    t: TFunction;
    language: string;
    hasUserPosition: boolean;
}

export function useParticipantMarkers({
    mapRef,
    participants,
    roomSession,
    t,
    language,
    hasUserPosition
}: UseParticipantMarkersProps) {
    const participantMarkersRef = useRef<Map<string, L.Marker>>(new Map());
    const participantCirclesRef = useRef<Map<string, L.Circle>>(new Map());

    const getParticipantRadius = (participant: Participant) => {
        const rawValue = participant.ac;

        if (rawValue === undefined || rawValue === null || !Number.isFinite(rawValue) || rawValue <= 0) {
            return 15;
        }

        return Math.max(rawValue, 5);
    };



    // Cleanup: clean refs when map is destroyed
    useEffect(() => {
        const markers = participantMarkersRef.current;
        const circles = participantCirclesRef.current;
        return () => {
            markers.clear();
            circles.clear();
        };
    }, []);

    // Remove participant markers when there is no session
    useEffect(() => {
        if (!mapRef.current) return;

        if (!roomSession || !hasUserPosition) {
            // Remove all participant markers when there is no session
            participantMarkersRef.current.forEach((marker) => {
                marker.remove();
            });
            participantMarkersRef.current.clear();
            participantCirclesRef.current.forEach((circle) => {
                circle.remove();
            });
            participantCirclesRef.current.clear();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomSession, hasUserPosition]);

    // Participant markers
    useEffect(() => {
        if (!mapRef.current || !roomSession || !hasUserPosition) return;

        const currentMarkers = participantMarkersRef.current;
        const currentCircles = participantCirclesRef.current;
        const currentIds = new Set(participants.map(p => p.id));

        // Remove markers of participants who left
        currentMarkers.forEach((marker, id) => {
            if (!currentIds.has(id)) {
                marker.remove();
                currentMarkers.delete(id);
            }
        });

        currentCircles.forEach((circle, id) => {
            if (!currentIds.has(id)) {
                circle.remove();
                currentCircles.delete(id);
            }
        });

        // Add/update participant markers
        participants.forEach((participant, index) => {
            const { id, name, position } = participant;

            const markerColor = getMarkerColor(index);

            if (!position || position.lt === 0 || position.lg === 0) return;

            const existingMarker = currentMarkers.get(id);
            const existingCircle = currentCircles.get(id);
            const radius = getParticipantRadius(participant);
            const popupContent = getParticipantPopupContent(participant, radius, t);

            const divIconOptions: L.DivIconOptions = {
                className: 'custom-marker',
                html: renderToString(<VisitorMarker
                    primaryColor={markerColor}
                    secondaryColor={markerColor}
                    text={name[0].toUpperCase()} />),
                iconSize: [32, 32],
                iconAnchor: [16, 16],
                popupAnchor: [0, -10]
            }

            if (existingMarker) {
                // Update position, popup and icon
                existingMarker.setLatLng([position.lt, position.lg]);
                existingMarker.setPopupContent(popupContent);

                // Update icon to reflect current translation
                const updatedIcon = L.divIcon(divIconOptions);
                existingMarker.setIcon(updatedIcon);

                if (existingCircle) {
                    existingCircle.setLatLng([position.lt, position.lg]);
                    existingCircle.setRadius(radius ?? 5);
                    existingCircle.setStyle({
                        color: markerColor,
                        fillColor: markerColor,
                        fillOpacity: 0.2,
                        weight: 1
                    });
                }
            } else {
                // Create marker
                const icon = L.divIcon(divIconOptions);

                const marker = L.marker([position.lt, position.lg], {
                    icon
                }).addTo(mapRef.current!);

                marker.bindPopup(popupContent);
                currentMarkers.set(id, marker);

                const circle = L.circle([position.lt, position.lg], {
                    radius: radius ?? 5,
                    color: markerColor,
                    fillColor: markerColor,
                    fillOpacity: 0.2,
                    weight: 1
                }).addTo(mapRef.current!);

                currentCircles.set(id, circle);
            }
        });
        // Trigger popup/icon refresh when language changes.
    }, [participants, roomSession, language, mapRef, hasUserPosition, t]);
}
